/**
 * One-time versioned backfill for the PAN-1908 agents table.
 *
 * This is the ONLY module permitted to enumerate
 * `${OVERDECK_HOME}/agents/{id}/state.json`. It is invoked:
 *
 *   - automatically once during the v54 -> v55 schema migration, and
 *   - manually via `pan admin db rebuild-agents`.
 *
 * Keeping the enumeration here makes it easy to audit that no hot path
 * (dashboard handlers, deacon patrol, read model) reads agent state from the
 * filesystem.
 */

import { readFileSync, readdirSync, statSync } from 'node:fs';
import { join } from 'node:path';
import { execFileSync } from 'node:child_process';
import { getOverdeckHome } from '../paths.js';
import type { SqliteDatabase } from './driver.js';
import { getDatabase } from './index.js';
import { agentStateToDbAgent } from './agent-mappers.js';
import type { AgentState } from '../agents.js';
import type { Agent as DbAgent } from './agents-db.js';
import { readAgentHarnessModelRecordSync } from '../overdeck/agent-record-sync.js';
import type { RuntimeName } from '../runtimes/types.js';

const VALID_ROLES = new Set<AgentState['role']>([
  'plan',
  'work',
  'review',
  'test',
  'ship',
  'flywheel',
  'strike',
  'knowledge',
]);

const COLUMN_MAP: Record<keyof DbAgent, string> = {
  id: 'id',
  issueId: 'issue_id',
  role: 'role',
  status: 'status',
  workspace: 'workspace',
  harness: 'harness',
  model: 'model',
  branch: 'branch',
  sessionId: 'session_id',
  startedAt: 'started_at',
  lastActivity: 'last_activity',
  lastResumeAt: 'last_resume_at',
  stoppedAt: 'stopped_at',
  stoppedByUser: 'stopped_by_user',
  stoppedByPause: 'stopped_by_pause',
  kickoffDelivered: 'kickoff_delivered',
  hostOverride: 'host_override',
  costSoFar: 'cost_so_far',
  phase: 'phase',
  workType: 'work_type',
  paused: 'paused',
  pausedReason: 'paused_reason',
  pausedAt: 'paused_at',
  yieldedByScheduler: 'yielded_by_scheduler',
  yieldedAt: 'yielded_at',
  lastYieldResumeAt: 'last_yield_resume_at',
  troubled: 'troubled',
  troubledAt: 'troubled_at',
  consecutiveFailures: 'consecutive_failures',
  firstFailureInRunAt: 'first_failure_in_run_at',
  lastFailureAt: 'last_failure_at',
  lastFailureReason: 'last_failure_reason',
  lastFailureNextRetryAt: 'last_failure_next_retry_at',
  flywheelRunId: 'flywheel_run_id',
  roleRunHead: 'role_run_head',
  reviewSubRole: 'review_sub_role',
  reviewRunId: 'review_run_id',
  reviewSynthesisAgentId: 'review_synthesis_agent_id',
  reviewOutputPath: 'review_output_path',
  reviewDeadlineAt: 'review_deadline_at',
  reviewMonitorSignaled: 'review_monitor_signaled',
  reviewRetryAttempt: 'review_retry_attempt',
  inspectSubRole: 'inspect_sub_role',
  deliveryMethod: 'delivery_method',
  supervisorEnabled: 'supervisor_enabled',
  channelsEnabled: 'channels_enabled',
  updatedAt: 'updated_at',
};

function getAgentDir(agentId: string): string {
  return join(getOverdeckHome(), 'agents', agentId);
}

function getManagedTmuxSocketName(): string {
  return process.env.OVERDECK_TMUX_SOCKET_NAME ?? 'overdeck';
}

function listLiveTmuxSessionNames(): Set<string> {
  try {
    const output = execFileSync(
      'tmux',
      ['-L', getManagedTmuxSocketName(), 'list-sessions', '-F', '#{session_name}'],
      { encoding: 'utf-8', stdio: ['ignore', 'pipe', 'ignore'] },
    );
    return new Set(
      output
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    );
  } catch {
    // tmux not running or unavailable — treat every agent as not live
    return new Set();
  }
}

function parseAgentStateJson(content: string, fallbackId: string): AgentState | null {
  let parsed: Partial<AgentState>;
  try {
    parsed = JSON.parse(content) as Partial<AgentState>;
  } catch {
    return null;
  }

  if (!parsed.role || !VALID_ROLES.has(parsed.role)) {
    return null;
  }

  if (!parsed.id) {
    parsed.id = fallbackId;
  }

  if (!parsed.status) {
    parsed.status = 'stopped';
  }

  // Type cast is safe: we validated role and ensured id/status are strings.
  return parsed as AgentState;
}

function buildNamedParams(row: DbAgent): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  for (const [key, column] of Object.entries(COLUMN_MAP)) {
    let value = (row as unknown as Record<string, unknown>)[key];
    if (typeof value === 'boolean') {
      value = value ? 1 : 0;
    }
    params[column] = value;
  }
  return params;
}

export interface BackfillAgentsOptions {
  verbose?: boolean;
  /**
   * Override tmux session discovery. Defaults to the real socket query.
   * Useful for tests and for environments where tmux is unavailable.
   */
  listLiveSessions?: () => Set<string>;
  /**
   * Override per-issue record lookup for harness/model. Defaults to
   * readAgentHarnessModelRecordSync. Useful for tests.
   */
  readHarnessModel?: (issueId: string) => { harness?: RuntimeName; model?: string } | null;
}

export interface BackfillAgentsResult {
  processed: number;
  skipped: number;
  markedStopped: number;
}

/**
 * Enumerate agent state files and upsert them into the agents table.
 *
 * Idempotent by id — re-running creates no duplicates. Status is reconciled
 * against live tmux sessions: an agent whose state says running/starting but
 * has no live session is marked stopped.
 */
export function backfillAgentsFromStateJsonSync(
  db: SqliteDatabase,
  options?: BackfillAgentsOptions,
): BackfillAgentsResult {
  const agentsDir = join(getOverdeckHome(), 'agents');
  const liveSessions = options?.listLiveSessions?.() ?? listLiveTmuxSessionNames();
  const readHarnessModel = options?.readHarnessModel ?? readAgentHarnessModelRecordSync;
  let processed = 0;
  let skipped = 0;
  let markedStopped = 0;

  let entries: string[] = [];
  try {
    entries = readdirSync(agentsDir);
  } catch {
    return { processed, skipped, markedStopped };
  }

  const columns = Object.values(COLUMN_MAP);
  const placeholders = columns.map((col) => `$${col}`).join(', ');
  const upsert = db.prepare(
    `INSERT OR REPLACE INTO agents (${columns.join(', ')}) VALUES (${placeholders})`,
  );

  const tx = db.transaction(() => {
    for (const entry of entries) {
      const dirPath = join(agentsDir, entry);
      let statePath: string;
      try {
        if (!statSync(dirPath).isDirectory()) continue;
        statePath = join(dirPath, 'state.json');
      } catch {
        continue;
      }

      let content: string;
      try {
        content = readFileSync(statePath, 'utf-8');
      } catch {
        skipped++;
        continue;
      }

      const state = parseAgentStateJson(content, entry);
      if (!state) {
        skipped++;
        continue;
      }

      const reconciled = reconcileAgentStatus(state, liveSessions);
      if (reconciled.status === 'stopped' && state.status !== 'stopped') {
        markedStopped++;
      }

      // PAN-1919: prefer harness/model from the per-issue git-tracked record so
      // rebuild-agents sources from the travelling record, not machine-local state.
      let effective = reconciled;
      if (reconciled.issueId) {
        const rec = readHarnessModel(reconciled.issueId);
        if (rec?.harness || rec?.model) {
          effective = { ...reconciled };
          if (rec.harness) effective.harness = rec.harness;
          if (rec.model) effective.model = rec.model;
        }
      }

      const row = agentStateToDbAgent(effective);
      upsert.run(buildNamedParams(row));
      processed++;

      if (options?.verbose) {
        console.log(`[backfill] ${row.id} -> ${row.status}`);
      }
    }
  });

  tx();

  return { processed, skipped, markedStopped };
}

function reconcileAgentStatus(
  state: AgentState,
  liveSessions: Set<string>,
): AgentState {
  if ((state.status === 'running' || state.status === 'starting') && !liveSessions.has(state.id)) {
    return {
      ...state,
      status: 'stopped',
      stoppedAt: state.stoppedAt ?? new Date().toISOString(),
    };
  }
  return state;
}

export function backfillAgentsAutoSync(options?: BackfillAgentsOptions): BackfillAgentsResult {
  return backfillAgentsFromStateJsonSync(getDatabase(), options);
}
